import type { ExhibitionFields } from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';
import { useReplaceExhibitionImage } from './use-replace-exhibition-image';

const IMAGE_STEP_FAILED = '이름·목적·납기는 저장했습니다. 대표 이미지만 다시 시도해 주세요.';

interface UseUpdateExhibitionOptions {
  readonly onSuccess?: () => void;
}

/** 이름·목적·납기를 바꾸고, 새 이미지를 골랐으면 그 뒤에 대표 이미지를 교체한다. */
export const useUpdateExhibition = ({ onSuccess }: UseUpdateExhibitionOptions = {}) => {
  const mutation = trpc.productManagement.exhibitions.update.useMutation({ onSuccess });
  const replaceAction = useReplaceExhibitionImage({ onSuccess });
  const [isImageStepFailed, setIsImageStepFailed] = useState(false);

  /**
   * update 를 먼저 보내고 성공한 뒤에만 이미지 단계를 보낸다 — update 가 실패하면 이미지는 올리지 않는다.
   * 이미지 단계만 실패하면 false 를 돌려줘 모달을 열어 둔 채 안내를 남긴다.
   * 다시 제출하면 같은 순서로 둘 다 보낸다(update 는 같은 값이라 결과가 같다).
   */
  const updateExhibition = async (
    id: string,
    fields: ExhibitionFields,
    file: File | null,
  ): Promise<boolean> => {
    setIsImageStepFailed(false);

    try {
      await mutation.mutateAsync({ id, ...fields });
    } catch {
      return false;
    }

    if (file === null) {
      return true;
    }

    if (!(await replaceAction.replaceImage(id, file))) {
      setIsImageStepFailed(true);
      return false;
    }

    return true;
  };

  // 원인은 replaceAction.errorMessage 가 이미 가지고 있다(use-create-exhibition.ts 와 같은 우선순위 —
  // 업로드 사전 검사 실패가 먼저, 없으면 서버 거부 메시지). 실패 순간이 아니라 매 렌더 다시 읽어야
  // update 성공 이후 커밋되는 replaceAction 의 상태를 놓치지 않는다.
  const imageStepError = isImageStepFailed
    ? replaceAction.errorMessage === null
      ? IMAGE_STEP_FAILED
      : `${IMAGE_STEP_FAILED} (${replaceAction.errorMessage})`
    : null;

  return {
    updateExhibition,
    isPending: mutation.isPending || replaceAction.isPending,
    /** 모달에 그대로 보여 줄 문구 — 이미지 단계 안내가 먼저, 없으면 update 서버 거부 메시지. */
    errorMessage: imageStepError ?? mutation.error?.message ?? null,
    reset: () => {
      setIsImageStepFailed(false);
      mutation.reset();
      replaceAction.reset();
    },
  };
};
